import {
  colors,
  createTheme as createMuiTheme,
  responsiveFontSizes,
  ThemeOptions,
} from '@mui/material';
import typography from './typography';
import { softShadows, strongShadows } from './shadows';
import { THEMES } from 'src/constants';
import { Settings } from 'src/interfaces/Settings';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    dark: string;
    card: string;
  }
}

const baseConfig: ThemeOptions = {
  direction: 'ltr',
  typography,
  components: {
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          paddingBottom: 0,
          paddingTop: 0,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 32,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0,0,0,0.075)',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          height: 'inherit',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          //backgroundColor: 'rgb(93, 135, 255)',
        },
      },
    },
  },
};

const getTheme = (theme: keyof typeof THEMES): ThemeOptions => {
  return theme === 'LIGHT'
    ? {
        components: {
          ...baseConfig.components,
          MuiFormControlLabel: {
            styleOverrides: {
              label: {
                color: colors.blueGrey[900],
              },
            },
          },
          MuiInputBase: {
            styleOverrides: {
              input: {
                '&::placeholder': {
                  opacity: 1,
                  color: colors.blueGrey[600],
                },
              },
            },
          },
          MuiTypography: {
            styleOverrides: {
              body1: {
                //color: colors.blueGrey[900],
              },
            },
          },
        },
        palette: {
          mode: 'light',
          action: {
            disabledBackground: 'rgba(73,82,88,0.12)',
            hoverOpacity: 0.02,
            hover: '#f6f9fc',
            //selected: 'white',
          },
          divider: '#e5eaef',
          background: {
            default: '#ffffff',
            dark: '#f4f6f8',
            paper: '#ffffff',
            card: '#eaeef2',
          },
          primary: {
            main: '#00549A',
            light: '#ECF2FF',
            dark: '#4570EA',
          },
          secondary: {
            main: '#49BEFF',
            light: '#E8F7FF',
            dark: '#23afdb',
          },
          success: {
            main: '#13DEB9',
            light: '#E6FFFA',
            dark: '#02b3a9',
            contrastText: '#ffffff',
          },
          info: {
            main: '#539BFF',
            light: '#EBF3FE',
            dark: '#1682d4',
            contrastText: '#ffffff',
          },
          error: {
            main: '#d32f2f',
            light: '#FDEDE8',
            dark: '#f3704d',
            contrastText: '#ffffff',
          },
          warning: {
            main: '#FFAE1F',
            light: '#FEF5E5',
            dark: '#ae8e59',
            contrastText: '#ffffff',
          },
          /*purple: {
            A50: '#EBF3FE',
            A100: '#6610f2',
            A200: '#557fb9',
          },*/
          grey: {
            100: '#F2F6FA',
            200: '#EAEFF4',
            300: '#DFE5EF',
            400: '#7C8FAC',
            500: '#5A6A85',
            600: '#2A3547',
          },
          text: {
            primary: '#2A3547',
            secondary: '#5A6A85',
          },
        },
        shadows: softShadows,
      }
    : {
        components: {
          ...baseConfig.components,
          MuiFormControlLabel: {
            styleOverrides: {
              label: {
                color: '#e6e5e8',
              },
            },
          },
          MuiTypography: {
            styleOverrides: {
              body1: {
                color: '#e6e5e8',
              },
            },
          },
        },
        palette: {
          mode: 'dark',
          action: {
            active: 'rgba(255, 255, 255, 0.54)',
            hover: 'rgba(255, 255, 255, 0.04)',
            selected: 'rgba(255, 255, 255, 0.08)',
            disabled: 'rgba(255, 255, 255, 0.26)',
            disabledBackground: 'rgba(255, 255, 255, 0.12)',
            focus: 'rgba(255, 255, 255, 0.12)',
          },
          background: {
            default: '#282C34',
            dark: '#1c2025',
            paper: '#282C34',
            card: '#333842',
          },
          primary: {
            main: '#8a85ff',
          },
          secondary: {
            main: '#8a85ff',
          },
          text: {
            primary: '#e6e5e8',
            secondary: '#adb0bb',
          },
        },
        shadows: strongShadows,
      };
};

export function createTheme(settings: Settings) {
  const mergedTheme: ThemeOptions = {
    ...baseConfig,
    ...getTheme('LIGHT'),
    ...{ direction: settings.direction },
  };

  let theme = createMuiTheme(mergedTheme);

  if (settings.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
}
